import { MediaText } from 'editor/src/store/design/types';

import { buildQrContentFromConfig, buildQrPayload, isContentComplete } from 'editor/src/lib/qr';
import { RootState } from 'product-personalizer/src/store';

export enum PersonalizationFieldErrorCode {
  Required = 'required',
  MaxCharCount = 'maxCharCount',
  QrPending = 'qrPending',
}

export type PersonalizationErrors = Record<number, PersonalizationFieldErrorCode>;

const getPersonalizationErrors = (state: RootState): PersonalizationErrors => {
  const errors: PersonalizationErrors = {};
  const { designData } = state.design;
  if (!designData) {
    return errors;
  }

  const texts: Record<number, string> = {};
  designData.spreads.forEach((spread) =>
    spread.pages.forEach((page) =>
      (page.groups.media ?? []).forEach((element) => {
        if (element.type === 'text' && !element.personalizationLocked) {
          texts[element.uuid] = (element as MediaText).extra?.text ?? '';
        }
      }),
    ),
  );

  designData.spreads.forEach((spread) =>
    spread.pages.forEach((page) =>
      (page.groups.media ?? []).forEach((element) => {
        // A QR element whose current data is not yet baked into its image (generation
        // debounced, upload in flight, or generation failed) must block the order —
        // otherwise the design is submitted with a stale QR and misprinted.
        if (element.type === 'image' && element.qrCode?.fields.length && !element.hidden) {
          const content = buildQrContentFromConfig(element.qrCode.type, element.qrCode.fields, (id) => texts[id]);
          if (isContentComplete(content) && buildQrPayload(content) !== element.qrCode.encoded) {
            errors[element.uuid] = PersonalizationFieldErrorCode.QrPending;
          }
          return;
        }
        if (element.type !== 'text') {
          return;
        }
        // A reflow row that collapsed marks its members `hidden:true`, and a
        // Condition can do the same. Either way, the element produces no
        // pixels and the buyer cannot fill it in — so treating an empty
        // mandatory field on a hidden element as a validation error would
        // block checkout for content the buyer never sees. Skip hidden.
        if (element.hidden) {
          return;
        }
        const textElement = element as MediaText;
        const { lockBoxSize, maxCharCount, isMandatory, text } = textElement.extra;

        if (isMandatory && text.trim().length === 0) {
          errors[textElement.uuid] = PersonalizationFieldErrorCode.Required;
          return;
        }

        if (lockBoxSize && maxCharCount && text.length > maxCharCount) {
          errors[textElement.uuid] = PersonalizationFieldErrorCode.MaxCharCount;
        }
      }),
    ),
  );

  return errors;
};

export default getPersonalizationErrors;
